// OperatorShell.tsx — MEA-tyylinen ohjattu operaattorikuori
// Lämmin, mobile-first, bottom-nav (Tänään / Työ / AI / Oma)
import { useState, useEffect, type ReactNode, useRef } from 'react';
import { ShellContext } from '../shell-context';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../api/auth';
import { authFetch } from '../api/client';
import { MENU_ITEMS } from './MorePage';
import {
  applyChoice,
  baseTabsForRole,
  defaultTabCount,
  freeSlotChoices,
  isFixedTab,
  loadOverrides,
  loadTabCount,
  resetTab,
  resolveTabs,
  saveOverrides,
  TAB_COUNT_EVENT,
  type NavOverrides,
} from './navCustomization';
import { useWorkday, getElapsedSeconds, formatElapsed, syncWorkdayWithBackend } from './workdayStore';
import { useBackgroundTasks, getRemaining, formatTimer, dismissTask, removeTask } from './backgroundTaskStore';
import QuickNotes from './QuickNotes';
import { registerNativePush } from '../push/nativePush';
import { syncNativePresence } from '../presence/nativePresence';
import TodayBar from '../components/TodayBar';
// Ulkoasun arvot tulevat jaetusta lahteesta (shared/design-tokens.json) — sama
// tiedosto ohjaa natiivisovelluksen kuorta, ks. styles/designTokens.ts.
import {
  FONT_FAMILY,
  FONT_FAMILY_NARROW,
  TOKENS,
  rgba,
  shellBackgroundGradient,
  withSafeArea,
} from '../styles/designTokens';

// U-1 (6.7.2026): YKSI yhtenäinen kuori kaikille. Operaattorilla 4 välilehteä;
// johto (manager/admin/ceo) saa samaan kuoreen lisäksi Tilannekuvan ja Projektit
// — Tänään/Työ/AI ovat SAMAT aidot sivut kuin operaattoreilla (kellokortti,
// työtehtävien suoritus, AI). Ei enää erillistä "johdon ERP:tä" / kytkinhyppelyä.
//
// 20.8.2026: alapalkin paikat ovat mukautettavia. Pitkä painallus avaa
// Lisää-valikon vaihtoehdot ja valinta vaihtaa sen paikan sisällön. Eri
// ihmiset käyttävät eri sivuja päivittäin, eikä kaikkien tarvitse kiertää
// Lisää-valikon kautta joka kerta. Valinta on laitekohtainen (localStorage).
//
// 27.8.2026: Tänään/Työ/Lisää ovat kiinteitä paikkoja ja näkyvien paikkojen
// määrän (3–6) voi valita Asetuksista — paikkalistat kootaan
// navCustomization.baseTabsForRole:lla, ei enää kiinteinä vakioina tässä.

export default function OperatorShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const workday = useWorkday();
  const [elapsed, setElapsed] = useState('');
  const bgTasks = useBackgroundTasks();
  const runningTasks = bgTasks.filter(t => t.status === 'running');
  const doneTasks = bgTasks.filter(t => t.status === 'done');

  useEffect(() => {
    if (!workday) { setElapsed(''); return; }
    // Tauolla (21.8.2026) getElapsedSeconds ei kasva → kello seisoo itsestään.
    const tick = () => setElapsed(formatElapsed(getElapsedSeconds(workday)));
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [workday?.startedAt, workday?.pausedAt, workday?.pausedSecondsTotal]);

  // Periodinen workday-sync kaikilla sivuilla — huomaa jos toinen laite on
  // aloittanut/lopettanut työpäivän. Ajetaan 30s välein sekä tabin focus-eventissä.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const syncNow = async () => {
      try {
        const res = await authFetch('/time-tracking/me/active');
        if (!res.ok) return;
        // Backend voi palauttaa tyhjän vastauksen kun ei aktiivista sessiota
        const text = await res.text();
        let active: any = null;
        if (text && text.trim()) {
          try { active = JSON.parse(text); } catch { active = null; }
        }
        if (cancelled) return;
        // active on joko { id, startedAt, ... } tai null
        syncWorkdayWithBackend(active && active.startedAt ? active : null);
      } catch { /* hiljainen fail */ }
    };

    // Alku + polling
    syncNow();
    const interval = setInterval(syncNow, 30000); // 30s
    // Myös kun käyttäjä palaa välilehdelle
    const onFocus = () => syncNow();
    const onVisibility = () => { if (!document.hidden) syncNow(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user?.id]);

// 7.8.2026: iOS:n bounce-ylivieritys paljastaa dokumentin taustan (vanha styles.css
  // --bg-base #0d0e11) vaalean shellin ylä-/alapuolella → musta reuna
  // natiivikuoressa ja kotinäyttö-PWA:ssa. Vaihdetaan tausta shellin ajaksi.
  useEffect(() => {
    const html = document.documentElement.style;
    const body = document.body.style;
    const prev = { html: html.background, body: body.background };
    html.background = TOKENS.color.backgroundBase;
    body.background = TOKENS.color.backgroundBase;
    return () => { html.background = prev.html; body.background = prev.body; };
  }, []);

  // Natiivipush (7.8.2026): natiivisovelluksessa rekisteröi APNs/FCM-token
  // kirjautuneelle käyttäjälle. No-op selaimessa/PWA:ssa; ei koskaan heitä.
  useEffect(() => {
    if (!user) return;
    void registerNativePush();
    // Läsnäolo (8.8.2026): itsekorjaus — jos opt-in on päällä mutta natiivi-
    // valvonta ei ole konfiguroituna (uusi asennus), kytke uudelleen.
    void syncNativePresence();
  }, [user?.id]);

  // U-1: johto saa laajennetun välilehtirivin samaan kuoreen
  const isExec = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'ceo';

  // 27.8.2026: näkyvien paikkojen määrä valitaan Asetuksista (3–6,
  // laitekohtainen). Kuori kuuntelee tapahtumaa jotta alapalkki päivittyy
  // heti kun valinta tehdään Asetukset-sivulla — ilman sivun latausta.
  const [tabCount, setTabCount] = useState(() => loadTabCount(defaultTabCount(isExec)));
  useEffect(() => {
    const sync = () => setTabCount(loadTabCount(defaultTabCount(isExec)));
    sync();
    window.addEventListener(TAB_COUNT_EVENT, sync);
    return () => window.removeEventListener(TAB_COUNT_EVENT, sync);
  }, [isExec]);
  const BASE_TABS = baseTabsForRole(isExec, tabCount);

  // Mukautetut paikat. Valinta luetaan kerran; tallennus kirjoittaa molemmat.
  const [navOverrides, setNavOverrides] = useState<NavOverrides>(() => loadOverrides());
  // Mikä paikka on pitkässä painalluksessa auki (null = valikko kiinni)
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const longPress = useRef<number | null>(null);
  // 27.8.2026: kevyt ohimenevä vihje kun kiinteää paikkaa yrittää vaihtaa
  const [fixedHint, setFixedHint] = useState(false);
  const fixedHintTimer = useRef<number | null>(null);
  const showFixedHint = () => {
    setFixedHint(true);
    if (fixedHintTimer.current) clearTimeout(fixedHintTimer.current);
    fixedHintTimer.current = window.setTimeout(() => setFixedHint(false), 1600);
  };

  // Valitsimessa Lisää-valikon sivut + roolin oletussivut (AI; johdolla myös
  // Tilanne/Projektit) — oletussivut saa näin takaisin vaikka niiden paikka
  // olisi vaihdettu toiseksi tai piilotettu määrää pienentämällä (27.8.2026).
  const pickerChoices = [
    ...freeSlotChoices(isExec).filter(c => !MENU_ITEMS.some(m => m.path === c.path)),
    ...MENU_ITEMS,
  ];
  const TABS = resolveTabs(BASE_TABS, navOverrides, pickerChoices);

  const chooseForTab = (tabId: string, path: string) => {
    const next = applyChoice(navOverrides, BASE_TABS, tabId, path);
    setNavOverrides(next);
    saveOverrides(next);
    setPickerFor(null);
  };

  const resetForTab = (tabId: string) => {
    const next = resetTab(navOverrides, tabId);
    setNavOverrides(next);
    saveOverrides(next);
    setPickerFor(null);
  };

  // 19.8.2026 (desktop-skaalaus): polkubugi — `startsWith('/op')` osui myös
  // `/operator/*`-reitteihin (esim. /operator/weekly-summary), jolloin ne
  // putosivat vahingossa STANDARD-leveyskaistalle vaikka ne ovat U-3-sääntöisiä
  // klassisia sivuja — ja alavalikossa hehkui väärä välilehti ('today' eikä
  // 'more'). Tarkistetaan tasan `/op` tai `/op/`-alku.
  const path = location.pathname;
  const isOpRoute = path === '/op' || path.startsWith('/op/');
  const isExecRoute = path === '/exec' || path.startsWith('/exec/');

  // 1.6.2026: lisätty /op/calendar, /op/deviations, /op/settings More-tabin
  // alle — aiemmin nämä näkyivät virheellisesti "Tänään"-tabissa aktiivisina.
  const MORE_TAB_PATHS = ['/op/me', '/op/inventory', '/op/tilaukset', '/op/suolasumutestit', '/op/more', '/op/baths', '/op/calendar', '/op/deviations', '/op/settings', '/op/asetukset', '/op/messages', '/op/projektit', '/op/projects', '/exec/more'];
  const activeTabRaw = TABS.find(t => path === t.path)?.id
    ?? (path.startsWith('/op/work') ? 'work'
      // 27.8.2026: AI-paikka voi olla piilotettu tai vaihdettu — jos 'ai' ei
      // ole näkyvissä, alempi TABS.some-tarkistus pudottaa 'more':en.
      : path.startsWith('/op/ai') ? 'ai'
      : path.startsWith('/manager-dashboard') ? 'tilannekuva'
      : path.startsWith('/projects') ? 'projects'
      : MORE_TAB_PATHS.some(p => path.startsWith(p)) ? 'more'
      // 6.7.2026 (bugijahti): kuoreen tuodut klassiset sivut (ei /op-/exec-polku) →
      // 'more' (niihin päädytään Lisää-sivulta) — EI 'today' joka hehkui väärin
      : (!isOpRoute && !isExecRoute) ? 'more'
      : 'today');
  // U-4: operaattorilla ei ole Projektit-välilehteä — yhdistetty /projects/:id
  // korostaa silloin Lisää-välilehden (sinne päädytään /op/projektit-listalta).
  const activeTab = TABS.some(t => t.id === activeTabRaw) ? activeTabRaw : 'more';

  // Huom: path / isOpRoute / isExecRoute määritellään ylempänä (activeTab-lohko)
  // — samaa polkupäättelyä käyttävät sekä välilehden korostus että leveyskaista.

  return (
    <ShellContext.Provider value={true}>
    <div className="op-shell" style={styles.root}>
      {/* 🖥 Desktop-skaalaus (19.8.2026): appi on mobile-first ja kaikki mitoitus
          on inline-px:iä → isolla näytöllä UI näyttää pieneltä zoomatulta
          puhelinsovellukselta. Ratkaisu: CSS zoom -portaat kuoren juureen.
          Kertoimet on laskettu niin että sisältökaista (1400 + 2×20 padding)
          mahtuu joka portaalla: breakpoint / kerroin ≥ 1440.
          Alaraja 1536 px pitää kaikki puhelimet ja iPadit ennallaan.
          CSS zoom EI vaikuta media queryihin eikä window.innerWidthiin →
          portaat eivät rekursoi. Sama arvo myös --app-zoom-muuttujaan, jotta
          JS (getAppZoom) ja vw/vh-mitoitukset voivat kompensoida.
          :root-taso saa VAIN muuttujan (ei zoom-ominaisuutta) — sitä tarvitsevat
          kuoren ulkopuoliset kiinteät elementit (esim. WorkReminder). */}
      <style>{`
        :root { --app-zoom: 1; }
        .op-shell { --app-zoom: 1; }
        @media (min-width: 1400px) { :root { --app-zoom: 1.1;  } .op-shell { --app-zoom: 1.1;  zoom: 1.1;  } }
        @media (min-width: 1600px) { :root { --app-zoom: 1.2;  } .op-shell { --app-zoom: 1.2;  zoom: 1.2;  } }
        @media (min-width: 1900px) { :root { --app-zoom: 1.35; } .op-shell { --app-zoom: 1.35; zoom: 1.35; } }
        @media (min-width: 2400px) { :root { --app-zoom: 1.55; } .op-shell { --app-zoom: 1.55; zoom: 1.55; } }
        @media print { :root { --app-zoom: 1; } .op-shell { --app-zoom: 1; zoom: 1; } }
      `}</style>

      {/* Top bar — minimal */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.logo}>SAVROC</span>
          <span style={styles.dot}>.</span>
        </div>
        <div style={styles.headerRight}>
          {/* U-1 (6.7.2026): "Johdon ERP →" -kytkin poistettu — yksi yhtenäinen
              kokemus, johdon lisäsivut ovat saman kuoren välilehtinä. */}
          {workday && elapsed && (
            // Tauolla (21.8.2026): sykkivä vihreä piste vaihtuu ⏸-merkkiin ja
            // kello himmenee — pysähtynyt aika näkyy pysähtyneenä joka sivulla.
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} title={workday.pausedAt ? 'Tauolla — kello pysähdyksissä' : undefined}>
              {workday.pausedAt ? (
                <span aria-label="Tauolla" style={{ fontSize: 12, lineHeight: 1 }}>⏸</span>
              ) : (
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a', animation: 'pulse 2s ease-in-out infinite' }} />
              )}
              <span style={{ fontSize: TOKENS.font.body, fontWeight: 800, color: workday.pausedAt ? TOKENS.color.textMuted : TOKENS.color.textPrimary, fontFamily: "var(--font-mono, monospace)" }}>{elapsed}</span>
              <style>{`@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }`}</style>
            </div>
          )}
          {!workday && (
            <span style={styles.greeting}>{user?.fullName?.split(' ')[0] ?? ''}</span>
          )}
        </div>
      </header>

      {/* 20.7.2026 (Aleksanterin pyyntö): "Kesken: …" -ilmoituspalkki poistettu
          toistaiseksi käytöstä — paluu keskeneräiseen työhön tapahtuu Työ-sivun
          kautta. Komponentti (InProgressWorkBanner) on tallella, palautus =
          <InProgressWorkBanner /> takaisin tähän + import. */}

      {/* Taustatehtävät — ajastimet + ilmoitukset */}
      {(runningTasks.length > 0 || doneTasks.length > 0) && (
        <div style={{ padding: '4px 16px', background: '#f0fdf4', borderBottom: '1px solid #bbf7d0', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
          {runningTasks.map(t => {
            const rem = getRemaining(t);
            const isUrgent = rem >= 0 && rem < 60;
            return (
              <div key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px',
                  background: isUrgent ? '#fef2f2' : TOKENS.color.surface, border: `1px solid ${isUrgent ? TOKENS.color.dangerBorder : TOKENS.color.rule}`,
                  borderRadius: 6, fontSize: 11,
                  animation: isUrgent ? 'pulse 1s ease-in-out infinite' : 'none',
                }}>
                <button onClick={() => navigate(t.route)} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, padding: 0 }}>
                  <span>⏱</span>
                  <span style={{ fontWeight: 600, color: isUrgent ? TOKENS.color.danger : TOKENS.color.textPrimary }}>
                    {rem >= 0 ? formatTimer(rem) : ''}
                  </span>
                  <span style={{ color: TOKENS.color.textSecondary, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.label}</span>
                </button>
                <button onClick={(e) => { e.stopPropagation(); removeTask(t.id); }}
                  style={{ background: 'none', border: 'none', color: TOKENS.color.textMuted, cursor: 'pointer', fontSize: 10, padding: '0 2px', lineHeight: 1 }}
                  title="Poista">✕</button>
              </div>
            );
          })}
          {doneTasks.map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: '#dcfce7', borderRadius: 6, fontSize: 11 }}>
              <span>✅</span>
              <button onClick={() => navigate(t.route)} style={{ background: 'none', border: 'none', color: '#16a34a', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', fontSize: 11 }}>
                {t.label} — valmis!
              </button>
              <button onClick={() => dismissTask(t.id)} style={{ background: 'none', border: 'none', color: TOKENS.color.textMuted, cursor: 'pointer', fontSize: 10 }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* 📋 Tänään-palkki (19.8.2026): päivän työt joka sivulla — klikkaus infonäytölle */}
      <TodayBar />

      {/* Content area — leveämpi kylvyt-sivuille, muuten mobiili-optimoitu 600px */}
      <main style={styles.content}>
        <div style={
          // 2.6.2026 (OP-WIDTH): kolmiportainen leveysstandardi
          // - WIDE (1400px): /op/baths, /op/calendar, /op/projektit — paljon visuaalista tilaa
          // - NARROW (600px): yksittäiset lomakkeet/chat (settings, me, more)
          // - STANDARD (800px): kaikki muut operaattorin sivut + AI-chat (oletus)
          (path.startsWith('/op/baths') || path.startsWith('/op/calendar')
            || path.startsWith('/op/projektit') || path.startsWith('/op/projects')
            // Lisää-valikot (19.8.2026): korttiruudukko täyttää leveän näytön
            || path.startsWith('/op/more') || path.startsWith('/exec/more')
            // Työ-sivu (19.8.2026): tehtäväruudukko hyötyy leveydestä desktopilla
            || path === '/op/work' || path.startsWith('/op/work/')
            // U-3: kuoreen tuodut klassiset sivut (ei /op- eikä /exec-polku) → leveä sisältö
            || (!isOpRoute && !isExecRoute))
            ? styles.contentInnerWide
            : (path.startsWith('/op/settings')
              || path.startsWith('/op/asetukset')
              || path.startsWith('/op/me'))
              ? styles.contentInner
              : styles.contentInnerStandard
        }>
          {children}
        </div>
      </main>

      {/* Pikamuistiot — kelluva nappi */}
      <QuickNotes />

      {/* Paikan sivuvalinta — aukeaa pitkästä painalluksesta alapalkissa.
          Ei modaali vaan alhaalta nouseva levy: se pysyy peukalon ulottuvilla
          eikä peitä sivua kokonaan, joten näkee mihin ollaan vaihtamassa. */}
      {pickerFor && (
        <>
          <div
            onClick={() => setPickerFor(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 60,
            }}
          />
          <div
            role="dialog"
            aria-label="Valitse sivu tähän paikkaan"
            style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 61,
              background: TOKENS.color.surface, borderRadius: '18px 18px 0 0',
              boxShadow: '0 -8px 32px rgba(15,23,42,0.18)',
              maxHeight: '70vh', display: 'flex', flexDirection: 'column',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            <div style={{ padding: '16px 20px 10px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                Valitse sivu tähän paikkaan
              </div>
              <div style={{ fontSize: TOKENS.font.label, color: TOKENS.color.textSecondary, marginTop: 3 }}>
                Valinta koskee vain tätä laitetta.
              </div>
            </div>

            <div style={{ overflowY: 'auto', padding: '8px 12px 12px' }}>
              {pickerChoices.map(item => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => chooseForTab(pickerFor, item.path)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                    padding: '13px 12px', minHeight: 48, border: 'none', borderRadius: 12,
                    background: 'transparent', textAlign: 'left', cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 22, flex: '0 0 auto' }}>{item.icon}</span>
                  <span style={{ fontSize: 15, color: '#0f172a', fontWeight: 600 }}>
                    {item.label}
                  </span>
                </button>
              ))}
            </div>

            <div style={{ borderTop: '1px solid #f1f5f9', padding: '10px 12px 14px', display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => resetForTab(pickerFor)}
                style={{
                  flex: 1, minHeight: 48, borderRadius: TOKENS.radius.navBtn, border: `1px solid ${TOKENS.color.rule}`,
                  background: TOKENS.color.surface, font: 'inherit', fontSize: 15, color: '#475569', cursor: 'pointer',
                }}
              >
                Palauta oletus
              </button>
              <button
                type="button"
                onClick={() => setPickerFor(null)}
                style={{
                  flex: 1, minHeight: 48, borderRadius: 12, border: 'none',
                  background: TOKENS.color.shellAccent, font: 'inherit', fontSize: 15, fontWeight: 600,
                  color: TOKENS.color.surface, cursor: 'pointer',
                }}
              >
                Sulje
              </button>
            </div>
          </div>
        </>
      )}

      {/* Kevyt vihje kiinteän paikan pitkästä painalluksesta (27.8.2026) —
          ei dialogia, häviää itsestään */}
      {fixedHint && (
        <div style={{
          position: 'fixed', left: '50%', transform: 'translateX(-50%)',
          bottom: 'calc(84px + env(safe-area-inset-bottom, 0px))', zIndex: 62,
          background: 'rgba(15,23,42,0.85)', color: TOKENS.color.surface, fontSize: TOKENS.font.caption,
          fontWeight: 600, padding: '8px 14px', borderRadius: 999,
          pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>
          Tämä paikka on kiinteä
        </div>
      )}

      {/* Bottom navigation — MEA-tyylinen */}
      <nav style={styles.bottomNav}>
        {TABS.map(tab => {
          const isActive = tab.id === activeTab;
          const isEmpty = !!tab.empty;
          return (
            <button
              key={tab.id}
              // Tyhjä "+"-paikka avaa sivuvalitsimen suoraan (27.8.2026)
              onClick={() => { if (isEmpty) setPickerFor(tab.id); else navigate(tab.path); }}
              // Pitkä painallus (600 ms) avaa sivuvalikon tälle paikalle.
              // Hiirellä sama onnistuu oikealla napilla — työpöydällä ei ole
              // pitkää painallusta. Kiinteille paikoille (Tänään/Työ/Lisää)
              // valitsinta ei avata — vain kevyt vihje (27.8.2026).
              onPointerDown={() => {
                longPress.current = window.setTimeout(() => {
                  longPress.current = null;
                  if (isFixedTab(tab.id)) showFixedHint();
                  else setPickerFor(tab.id);
                }, 600);
              }}
              onPointerUp={() => {
                if (longPress.current) { clearTimeout(longPress.current); longPress.current = null; }
              }}
              onPointerLeave={() => {
                if (longPress.current) { clearTimeout(longPress.current); longPress.current = null; }
              }}
              onContextMenu={e => {
                e.preventDefault();
                if (isFixedTab(tab.id)) { showFixedHint(); return; }
                setPickerFor(tab.id);
              }}
              style={{
                ...styles.navBtn,
                ...(isActive ? styles.navBtnActive : {}),
              }}
            >
              <span style={{
                fontSize: isActive ? 26 : 22, transition: 'font-size 0.2s',
                // Tyhjä paikka: kevyt harmaa "+" — kutsuu valitsemaan (27.8.2026)
                ...(isEmpty ? { color: TOKENS.color.navEmpty } : {}),
              }}>
                {tab.icon}
              </span>
              <span style={{
                ...styles.navLabel,
                color: isEmpty ? TOKENS.color.navEmpty : isActive ? TOKENS.color.shellAccent : TOKENS.color.textMuted,
                fontWeight: isActive ? 700 : 500,
              }}>
                {tab.label}
              </span>
              {isActive && <div style={styles.activeIndicator} />}
            </button>
          );
        })}
      </nav>
    </div>
    </ShellContext.Provider>
  );
}

// ── Styles ──

// Sisaltokaistan padding on sama kaikilla kolmella leveydella — yksi vakio,
// jotta tokenin muutos ei jaa vahingossa puolitiehen.
const CONTENT_PADDING = `${TOKENS.space.contentY}px ${TOKENS.space.contentX}px`;

const styles: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    // 19.8.2026 (desktop-skaalaus): vh EI skaalaudu zoomin mukana → 100vh olisi
    // 1.3×-portaalla 130vh korkea ja jokaiselle sivulle ilmestyisi turha
    // pystyvieritys. Jaetaan kertoimella.
    minHeight: 'calc(100vh / var(--app-zoom, 1))',
    // Leveyskuri (19.8.2026): mikään lapsi (nowrap-teksti, leveä taulukko,
    // banneri) ei saa leventää sivua mobiililla — ylivuoto leikkautuu kuoreen
    // ja leveä sisältö vierittyy omassa laatikossaan, ei koko sivuna.
    // 19.8.2026 (desktop-skaalaus): 100vw → 100% — vw EI skaalaudu zoomin
    // mukana, joten zoomatulla kuorella juuri leikkautuisi oikealta.
    maxWidth: '100%',
    overflowX: 'hidden',
    background: shellBackgroundGradient(),
    fontFamily: FONT_FAMILY,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `0 ${TOKENS.space.contentX}px`,
    // Natiivikuori/PWA: statuspalkki (kello, akku) piirtyy sivun päälle —
    // env(safe-area-inset-top) työntää sisällön sen alle. Selaimessa = 0.
    paddingTop: 'env(safe-area-inset-top, 0px)',
    height: withSafeArea(TOKENS.size.header, 'top'),
    // 28.8.2026 (#320-jatko): SPA:ssa EI ole globaalia box-sizing-resetiä
    // (vanhan styles.css:n `* { box-sizing: border-box }` ei ollut importattu mistään; tiedosto poistettiin 19.10.2026),
    // joten selaimen oletus content-box lisäsi safe-arean KAHDESTI: paddingTop
    // (env) + height (52px + env) summautuivat appikuoressa 52 + 2×~59 px
    // ≈ 170 px korkeaksi yläpalkiksi (selaimessa env = 0 → ei näkynyt).
    // border-box laskee env:n täsmälleen kerran: kokonaiskorkeus 52px + env,
    // SAVROC-rivi istuu aina 52 px:n kaistalla heti statuspalkin alla —
    // statuspalkki ei silti piirry tekstin päälle (#320 pysyy korjattuna).
    boxSizing: 'border-box',
    background: rgba(TOKENS.color.headerSurface),
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderBottom: `1px solid ${rgba(TOKENS.color.hairline)}`,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 0,
  },
  logo: {
    fontSize: TOKENS.font.title,
    fontWeight: 800,
    color: TOKENS.color.textPrimary,
    letterSpacing: '-0.02em',
  },
  dot: {
    fontSize: TOKENS.font.title,
    fontWeight: 800,
    color: TOKENS.color.shellAccent,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  greeting: {
    fontSize: TOKENS.font.label,
    color: TOKENS.color.textSecondary,
    fontWeight: 500,
  },
  backToMgmt: {
    fontSize: TOKENS.font.navLabel,
    fontWeight: 700,
    color: TOKENS.color.shellAccent,
    background: rgba(TOKENS.color.navActiveTint),
    border: `1px solid ${rgba(TOKENS.color.shellAccentBorder)}`,
    borderRadius: 6,
    padding: '5px 10px',
    cursor: 'pointer',
    fontFamily: FONT_FAMILY_NARROW,
    whiteSpace: 'nowrap',
  },
  logoutBtn: {
    fontSize: TOKENS.font.navLabel,
    color: TOKENS.color.textMuted,
    background: 'none',
    border: `1px solid ${TOKENS.color.rule}`,
    borderRadius: 6,
    padding: '4px 10px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden', // estä sivuttaisvieritys/marginaalien yli liike mobiilissa
    paddingBottom: withSafeArea(TOKENS.size.contentBottomInset, 'bottom'), // space for bottom nav + kotikahva
  },
  contentInner: {
    maxWidth: TOKENS.size.contentNarrow,
    width: '100%',
    margin: '0 auto',
    padding: CONTENT_PADDING,
    boxSizing: 'border-box', // padding ei kasvata leveyttä yli 100% → ei ylivuotoa
  },
  contentInnerWide: {
    maxWidth: TOKENS.size.contentWide,
    margin: '0 auto',
    padding: CONTENT_PADDING,
  },
  // 2.6.2026 (OP-WIDTH): vakiomittainen leveys useimmille operaattorin sivuille
  // (Tänään, Työ, Projektit, Varasto, Poikkeamat, Koeajot, 5L-wizardi).
  // 800 px — klassinen mobile-first leveys, sopii kortti- ja flow-näkymille.
  // Iteroitu: 1200 → 960 → 800 palautteen pohjalta.
  contentInnerStandard: {
    maxWidth: TOKENS.size.contentStandard,
    margin: '0 auto',
    padding: CONTENT_PADDING,
    width: '100%',
    boxSizing: 'border-box',
  },
  // 1.6.2026: täysleveä koko näytön kokoinen — kalenteri-sivulle jotta
  // kuukausi-grid hyödyntää desktop-näytön koko levyden
  contentInnerFull: {
    width: '100%',
    padding: '20px 24px',
    boxSizing: 'border-box',
  },
  bottomNav: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    background: rgba(TOKENS.color.bottomNavSurface),
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderTop: `1px solid ${rgba(TOKENS.color.hairline)}`,
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    // 28.8.2026: sama periaate kuin headerissä — safe-area täsmälleen kerran,
    // box-sizing kirjattu auki. Nappirivi on aina 72 px ja kotikahvan kaista
    // on paddingia sen alla (yhteensä 72px + env). Ilman eksplisiittistä
    // height-calcia + border-boxia lopputulos riippuisi siitä, injektoiko
    // jokin sivu globaalin box-sizing-resetin (border-box olisi litistänyt
    // napit 38 px:n kaistalle kotikahvan taakse).
    height: withSafeArea(TOKENS.size.bottomNav, 'bottom'),
    boxSizing: 'border-box',
    zIndex: 100,
  },
  navBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: `${TOKENS.space.navBtnY}px ${TOKENS.space.navBtnX}px`,
    borderRadius: TOKENS.radius.navBtn,
    position: 'relative',
    transition: 'background 0.2s',
    minWidth: TOKENS.size.navBtnMinWidth, // 6 välilehteä mahtuu iPhone SE:lle (bugijahti)
  },
  navBtnActive: {
    background: rgba(TOKENS.color.navActiveTint),
  },
  navLabel: {
    fontSize: TOKENS.font.navLabel,
    letterSpacing: '0.01em',
    fontFamily: FONT_FAMILY_NARROW,
    // 27.8.2026: pitkä mukautettu label ei saa levittää nappia eikä mennä
    // naapurin päälle 390 px leveydellä kun paikkoja on 6 — katkaistaan.
    maxWidth: TOKENS.size.navLabelMaxWidth,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  activeIndicator: {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: TOKENS.size.navIndicatorWidth,
    height: TOKENS.size.navIndicatorHeight,
    borderRadius: TOKENS.radius.navIndicator,
    background: TOKENS.color.navIndicator,
  },
};
